import { createColumnHelper } from "@tanstack/react-table";
import { PageHeading } from "@via/components/page-heading.tsx";
import Table, { DrawerSize } from "@via/components/table/table";
import {
  useAddExistingFilesToDataset,
  useDeleteDatasetFile,
  usePremiumDataset,
  usePremiumDatasetFile,
  usePremiumDatasetFileList,
  usePremiumDatasetFileListAll,
  useDownloadDatasetFiles,
} from "@via/pages/data/premium-dataset/premium-dataset.api.tsx";
import { BUTTONS, ROUTES } from "@via/utils/constants";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AddPremiumDatasetFile from "@via/pages/data/premium-dataset/components/add-premium-dataset-file";
import AddExistingFilesModal from "@via/pages/data/premium-dataset/components/add-existing-files";
import AddFolderPathModal from "@via/pages/data/premium-dataset/components/add-folder-path-modal";
import AddPremiumDatasetFieldsModal from "@via/pages/data/premium-dataset/components/add-premium-dataset-fields-modal";
import {
  FilterItem,
  HIDE_COLUMNS,
  renderCell,
} from "@via/pages/data/premium-dataset/utils/helper";
import useUrlParams from "@via/hooks/useURLParams";
import PremiumDatasetFileFilter from "@via/pages/data/premium-dataset/components/premium-dataset-file-filter.tsx";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import EditPremiumDatasetFile from "@via/pages/data/premium-dataset/components/edit-premium-dataset-file.tsx";
import {
  AddExistingFilesVmetaDataSetRequestBody,
  TaggableEntityType,
  UserRole,
  VmetaDataResponse,
} from "common";
import EntityTagsSection from "@via/components/entity-tags-section.tsx";
import IndeterminateCheckbox from "@via/components/inputs/indeterminate-checkbox";
import { SkeletonLoader } from "@via/components/loading/loading";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import ContextButton from "@via/components/context-button/context-button";
import { TableContextMenuInterface } from "@via/utils/interfaces";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import VmetaDownloadCartModal from "@via/pages/data/premium-dataset/components/vmeta-download-cart-modal";
import ColumnSelectorButton from "@via/components/column-selector-button/column-selector-button";
import {
  Cog6ToothIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import Drawer from "@via/components/drawer";
import EditPremiumDatasetForm from "@via/pages/data/premium-dataset/components/edit-premium-dataset-form.tsx";
import { useAuth } from "@via/hooks/useAuth";
import FileSourceCell from "@via/pages/data/premium-dataset/components/file-source-cell.tsx";
import {
  FILE_SOURCE_NAME_FIELD,
  FILE_SOURCE_PROVIDER_FIELD,
} from "@via/pages/data/premium-dataset/components/file-source-fields.ts";
import HowItWorksContent from "@via/pages/data/premium-dataset/components/how-it-works-content";
import { allowsDownloadFolder } from "@via/hooks/organization-access.ts";

const columnHelper = createColumnHelper<Record<string, unknown>>();

const defaultParams = {
  sort: [],
  filters: [],
  page: 0,
  pageSize: 10,
};

export default function PremiumDatasetFile(): JSX.Element {
  const { datasetId = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setSelectedCollectionId, setIsSelectedCollectionPremium } =
    useGlobal();

  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();

  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [columnFilters, setColumnFilters] = useState<{ or: FilterItem[] }>(
    initialParams.filters
  );

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});
  const [isAddDatasetFileModalOpen, setIsAddDatasetFileModalOpen] =
    useState(false);
  const [isAddFolderPathModalOpen, setIsAddFolderPathModalOpen] =
    useState(false);
  const [isAddExistingFilesModalOpen, setIsAddExistingFilesModalOpen] =
    useState(false);
  const [isAddFieldsModalOpen, setIsAddFieldsModalOpen] = useState(false);
  const [datasetFilesToBeDeleted, setDatasetFilesToBeDeleted] = useState<
    Record<string, unknown>[]
  >([]);
  const [projectIds, setProjectIds] = useState<string[]>([]);

  const [datasetFilesToBeEdited, setDatasetFilesToBeEdited] = useState<{
    data: VmetaDataResponse[];
    meta: {
      field_types: Record<string, unknown>[];
    };
  } | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Record<string, unknown>[]>(
    []
  );

  // Enhanced selection state for virtual selection
  const [isAllFilesSelected, setIsAllFilesSelected] = useState(false);
  const [totalFilesCount, setTotalFilesCount] = useState(0);
  const [deselectedFileIds, setDeselectedFileIds] = useState<Set<string>>(
    new Set()
  );

  const { data: datasetData, isLoading: isDatasetLoading } =
    usePremiumDataset(datasetId);

  const isDatasetOwner =
    !!user?.vmeta?.userId &&
    !!datasetData?.data?.owner?._id &&
    String(datasetData.data.owner._id) === String(user.vmeta.userId);

  const isGuest = user?.userRole === UserRole.Guest;
  const isDownloadFolderEnabled = allowsDownloadFolder(user);

  const addButtonOptions = useMemo(() => {
    if (isGuest) {
      return isDownloadFolderEnabled
        ? [{ name: BUTTONS.ADD_FOLDER_PATH, href: "#" }]
        : [];
    }

    return [
      { name: BUTTONS.ADD_NEW_FILE, href: "#" },
      { name: BUTTONS.IMPORT_FROM_SPREADSHEET, href: "#" },
      ...(isDownloadFolderEnabled
        ? [{ name: BUTTONS.ADD_FOLDER_PATH, href: "#" }]
        : []),
      { name: BUTTONS.LINK_EXISTING_FILES, href: "#" },
      { name: BUTTONS.ADD_FIELDS, href: "#" },
    ];
  }, [isGuest, isDownloadFolderEnabled]);

  const isAddButtonDisabled = isGuest && !isDownloadFolderEnabled;

  const {
    data,
    isLoading,
    refetch: refetchPremiumDatasetFiles,
  } = usePremiumDatasetFileList(datasetId, page, pageSize, sort, columnFilters);

  const {
    mutateAsync: deleteDatasetFile,
    isPending: isDeleteDatasetFileLoading,
  } = useDeleteDatasetFile();

  const {
    mutateAsync: fetchDatasetFile,
    isPending: isFetchDatasetFileLoading,
  } = usePremiumDatasetFile();

  const { mutateAsync: addExistingFilesToDataset } =
    useAddExistingFilesToDataset();

  const { mutateAsync: downloadDatasetFiles, isPending: isDownloadLoading } =
    useDownloadDatasetFiles();

  const { mutateAsync: fetchAllDatasetFiles, isPending: isFetchingAllFiles } =
    usePremiumDatasetFileListAll();

  const [isDownloadCartModalOpen, setIsDownloadCartModalOpen] = useState(false);
  const [downloadCartFiles, setDownloadCartFiles] = useState<
    Record<string, unknown>[]
  >([]);
  const [isDatasetSettingsOpen, setIsDatasetSettingsOpen] = useState(false);

  // Update total files count when data changes
  useEffect(() => {
    if (data?.results) {
      setTotalFilesCount(data.results);
    }
  }, [data?.results]);

  useEffect(() => {
    setUrlParams({
      sort,
      filters: [],
      page,
      pageSize,
    });
  }, [sort, page, pageSize]);

  const getPremiumDatasetFileContext = useCallback(() => {
    const datasetFiles = data?.data || [];
    return {
      datasetFiles,
      total: data?.results || 0,
      currentPage: page,
      pageSize: pageSize,
      filters: columnFilters,
      sortBy: sort.length > 0 ? sort[0] : null,
    };
  }, [data?.data, data?.results, page, pageSize, sort, columnFilters]);

  useRegisterCortexContext({
    routePattern: ROUTES.PREMIUM_DATASET_FILE,
    contextName: "Premium Dataset File",
    getContext: getPremiumDatasetFileContext,
    systemPrompt:
      "You are analyzing premium dataset file list page. Help answer questions about the premium dataset file list page.",
  });

  const toggleAllFilesSelected = useCallback(() => {
    if (isAllFilesSelected) {
      setSelectedFiles([]);
      setIsAllFilesSelected(false);
      setDeselectedFileIds(new Set());
    } else {
      // Select all files - set virtual selection state
      // We don't need to fetch all files, just mark that all are selected
      setIsAllFilesSelected(true);
      setDeselectedFileIds(new Set()); // Clear any previous deselections

      // Select all files on current page for immediate visual feedback
      const currentPageFiles = data?.data || [];
      setSelectedFiles(currentPageFiles);
    }
  }, [isAllFilesSelected, data?.data]);

  const handleFileToggle = useCallback(
    (file: Record<string, unknown>) => {
      const fileId = file._id as string;
      const isCurrentlySelected = isAllFilesSelected
        ? !deselectedFileIds.has(fileId)
        : selectedFiles.some(item => item._id === file._id);

      if (isCurrentlySelected) {
        // Deselecting a file
        if (isAllFilesSelected) {
          // Add to deselected set
          setDeselectedFileIds(prev => new Set([...prev, fileId]));
          // Remove from selectedFiles if it exists there
          setSelectedFiles(prev => prev.filter(item => item._id !== file._id));
        } else {
          // Normal deselection
          setSelectedFiles(prev => prev.filter(item => item._id !== file._id));
        }
      } else {
        // Selecting a file
        if (isAllFilesSelected) {
          // Remove from deselected set (if it was there)
          setDeselectedFileIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(fileId);
            return newSet;
          });
        } else {
          // Normal selection
          setSelectedFiles(prev => [...prev, file]);
        }
      }
    },
    [selectedFiles, isAllFilesSelected, deselectedFileIds]
  );

  const checkboxColumn = useMemo(
    () =>
      columnHelper.accessor("checkboxColumn", {
        header: () => (
          <IndeterminateCheckbox
            checked={isAllFilesSelected}
            indeterminate={
              !isAllFilesSelected &&
              selectedFiles.length > 0 &&
              selectedFiles.length < totalFilesCount
            }
            onChange={toggleAllFilesSelected}
            onClick={e => {
              e.stopPropagation();
            }}
            className="text-indigo-600"
          />
        ),
        cell: ({ row }) => (
          <IndeterminateCheckbox
            onClick={e => {
              e.stopPropagation();
            }}
            checked={
              (isAllFilesSelected &&
                !deselectedFileIds.has(row.original._id as string)) ||
              selectedFiles.some(({ _id }) => _id === row.original._id)
            }
            onChange={() => handleFileToggle(row.original)}
            className="text-indigo-600"
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
        size: 50,
      }),
    [
      selectedFiles,
      isAllFilesSelected,
      deselectedFileIds,
      totalFilesCount,
      toggleAllFilesSelected,
      handleFileToggle,
    ]
  );

  const columns = useMemo(() => {
    if (!data) return [];
    const columns =
      data?.meta?.field_types?.map(field => {
        return columnHelper.accessor(field?.name as string, {
          cell: info => (
            <span className="whitespace-nowrap px-1">
              {field?.name === FILE_SOURCE_PROVIDER_FIELD ? (
                <FileSourceCell
                  provider={info.getValue()}
                  name={
                    (
                      info.row.original as Record<string, unknown> | undefined
                    )?.[FILE_SOURCE_NAME_FIELD]
                  }
                />
              ) : (
                renderCell(info, field)
              )}
            </span>
          ),
          header: () => <span className="px-1">{field?.label as string}</span>,
        });
      }) || [];
    return [checkboxColumn, ...columns];
  }, [data, checkboxColumn]);

  const columnSelectorOptions = useMemo(
    () =>
      (data?.meta?.field_types || []).map(field => ({
        id: (field?.name as string) || "",
        label: (field?.label as string) || (field?.name as string) || "",
      })),
    [data?.meta?.field_types]
  );

  const handleColumnSelectorToggle = useCallback(
    (columnId: string, isVisible: boolean) => {
      if (!columnId) return;
      setColumnVisibility(prev => ({ ...prev, [columnId]: isVisible }));
    },
    []
  );

  useEffect(() => {
    if (data && Object.keys(columnVisibility).length === 0) {
      const initialVisibility = {};
      columns.forEach(col => {
        if (col.accessorKey !== "checkboxColumn") {
          initialVisibility[col.id ?? col.accessorKey ?? ""] =
            !HIDE_COLUMNS.includes(col.accessorKey ?? "");
        }
      });
      setColumnVisibility(initialVisibility);
    }
  }, [data, columns]);

  const initialCanvasIdForFieldsModal = useMemo(() => {
    const fromSelection = selectedFiles?.[0]?.canvasId as string | undefined;
    if (fromSelection) return fromSelection;
    return (data?.data?.[0]?.canvasId as string | undefined) ?? undefined;
  }, [selectedFiles, data?.data]);

  const handleOpenEditModal = async () => {
    const projectIds = selectedFiles.map(file => file.canvasId as string);
    setProjectIds(projectIds);

    // fetching only the first file to get the fields
    const firstFile = selectedFiles[0];
    const filesData = await fetchDatasetFile({
      id: firstFile._id as string,
      canvasId: firstFile.canvasId as string,
      collectionName: "file",
    });

    const files = {
      data: selectedFiles as VmetaDataResponse[],
      meta: {
        field_types: filesData?.meta?.field_types || [],
      },
    };

    setDatasetFilesToBeEdited(files);
  };

  const handleOpenDeleteModal = () => {
    setDatasetFilesToBeDeleted(selectedFiles);
  };

  const handleConfirmDelete = async () => {
    try {
      // Group selected files by canvasId in case they span multiple canvases
      const byCanvas = new Map<string, string[]>();
      for (const file of datasetFilesToBeDeleted) {
        const { canvasId, _id } = file as { canvasId: string; _id: string };
        if (!byCanvas.has(canvasId)) {
          byCanvas.set(canvasId, []);
        }
        byCanvas.get(canvasId)!.push(_id);
      }

      await Promise.all(
        Array.from(byCanvas.entries()).map(([canvasId, fileIds]) =>
          deleteDatasetFile({
            datasetId,
            canvasId,
            fileIds,
          })
        )
      );

      // Reset selection state
      setSelectedFiles([]);
      setIsAllFilesSelected(false);
      setDeselectedFileIds(new Set());
    } catch (error) {
      console.error("Error deleting files:", error);
    } finally {
      setDatasetFilesToBeDeleted([]);
    }
  };

  const handleExportAsSpreadsheet = useCallback(() => {
    downloadDatasetFiles({
      datasetId,
      datasetName: datasetData?.data?.name,
      filter:
        columnFilters?.or && columnFilters.or.length > 0
          ? columnFilters
          : undefined,
      sort,
    });
  }, [
    datasetId,
    datasetData?.data?.name,
    columnFilters,
    sort,
    downloadDatasetFiles,
  ]);

  const handleDownloadCloudFiles = useCallback(async () => {
    let filesToUse: Record<string, unknown>[];

    // Check isAllFilesSelected FIRST - this takes priority over visible selections
    // because user may have selected all and deselected some across pages
    if (isAllFilesSelected) {
      // Fetch all rows and filter out deselected ones
      const allData = await fetchAllDatasetFiles({
        datasetId,
        sort,
        filter:
          columnFilters?.or && columnFilters.or.length > 0
            ? columnFilters
            : undefined,
      });

      if (deselectedFileIds.size > 0) {
        // Filter out deselected files
        filesToUse = (allData?.data || []).filter(
          (file: Record<string, unknown>) =>
            !deselectedFileIds.has(file._id as string)
        );
      } else {
        filesToUse = allData?.data || [];
      }
    } else if (selectedFiles.length > 0) {
      // Manual selection mode - use only the selected files
      filesToUse = selectedFiles;
    } else {
      // No selection - fetch all rows
      const allData = await fetchAllDatasetFiles({
        datasetId,
        sort,
        filter:
          columnFilters?.or && columnFilters.or.length > 0
            ? columnFilters
            : undefined,
      });
      filesToUse = allData?.data || [];
    }

    setDownloadCartFiles(filesToUse);
    setIsDownloadCartModalOpen(true);
  }, [
    selectedFiles,
    isAllFilesSelected,
    deselectedFileIds,
    fetchAllDatasetFiles,
    datasetId,
    columnFilters,
    sort,
  ]);

  const handleDownloadAction = useCallback(
    (action: TableContextMenuInterface) => {
      switch (action.name) {
        case "Files":
          handleDownloadCloudFiles();
          break;
        case "Spreadsheet (Excel)":
          handleExportAsSpreadsheet();
          break;
        default:
          break;
      }
    },
    [handleDownloadCloudFiles, handleExportAsSpreadsheet]
  );

  const handleAddExistingFiles = async (
    _canvasId: string,
    payload: AddExistingFilesVmetaDataSetRequestBody
  ) => {
    try {
      await addExistingFilesToDataset({
        id: datasetId,
        ...payload,
      });
      // Reset selection state
      setSelectedFiles([]);
      setIsAllFilesSelected(false);
      setDeselectedFileIds(new Set());
    } catch (error) {
      console.error("Error adding existing files:", error);
    }
  };

  const handleAddFiles = () => {
    setSelectedCollectionId(datasetId);
    setIsSelectedCollectionPremium(true);
    navigate(`${ROUTES.CREATE_DATA_SOURCE}?datasetId=${datasetId}`);
  };

  const handleAddAction = (action: TableContextMenuInterface) => {
    switch (action.name) {
      case BUTTONS.ADD_NEW_FILE:
        handleAddFiles();
        break;
      case BUTTONS.IMPORT_FROM_SPREADSHEET:
        setIsAddDatasetFileModalOpen(true);
        break;
      case BUTTONS.ADD_FOLDER_PATH:
        setIsAddFolderPathModalOpen(true);
        break;
      case BUTTONS.LINK_EXISTING_FILES:
        setIsAddExistingFilesModalOpen(true);
        break;
      case BUTTONS.ADD_FIELDS:
        setIsAddFieldsModalOpen(true);
        break;
      default:
        break;
    }
  };

  return (
    <main className="w-full overflow-hidden">
      {isDatasetLoading ? (
        <div className="flex justify-center items-center h-full">
          <SkeletonLoader count={1} />
        </div>
      ) : (
        <div className="via-page-heading-wrapper">
          <PageHeading title={datasetData?.data?.name || ""} />
          <div className="flex gap-2">
            <ContextButton
              text="Add"
              options={addButtonOptions}
              onSelect={handleAddAction}
              disabled={isAddButtonDisabled}
              className="h-[38px] inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
              disabled={!selectedFiles.length}
              onClick={handleOpenEditModal}
              className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              Edit
            </button>
            <button
              disabled={!selectedFiles.length}
              onClick={handleOpenDeleteModal}
              className="inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600">
              Remove
            </button>
            {isDatasetOwner && (
              <button
                type="button"
                onClick={() => setIsDatasetSettingsOpen(true)}
                className="h-[38px] inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 items-center gap-1">
                <Cog6ToothIcon className="h-5 w-5" aria-hidden="true" />
                Settings
              </button>
            )}
            <ContextButton
              text="Download"
              options={[
                { name: "Files", href: "#" },
                { name: "Spreadsheet (Excel)", href: "#" },
              ]}
              onSelect={handleDownloadAction}
              loading={isDownloadLoading || isFetchingAllFiles}
              className="h-[38px] inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold !text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 items-center gap-1"
            />
          </div>
        </div>
      )}

      {datasetId && (
        <div className="px-4 pb-2">
          <EntityTagsSection
            entityType={TaggableEntityType.Dataset}
            entityId={datasetId}
            canEdit={isDatasetOwner || user?.userRole === UserRole.Admin}
          />
        </div>
      )}

      <div>
        <div className="overflow-auto h-100vh">
          <Table
            isLoading={isLoading}
            data={data?.data || []}
            columns={columns}
            sort={sort}
            setSort={setSort}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            total={data?.results || 0}
            emptyMessage="No data available."
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            displayCustomColumnVisibility={false}
            applyColumnSize
            customTableClassName="w-max whitespace-nowrap"
            additionalFilters={
              <PremiumDatasetFileFilter
                columns={columns}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                setPage={setPage}
                setPageSize={setPageSize}
                howItWorks={{
                  title: (
                    <div className="flex items-center gap-2 pr-10">
                      <InformationCircleIcon className="h-5 w-5 text-blue-700" />
                      <span className="text-xl font-semibold text-gray-900">
                        Datasets
                      </span>
                    </div>
                  ),
                  content: <HowItWorksContent />,
                }}
                rightActions={
                  columnSelectorOptions.length > 0 ? (
                    <ColumnSelectorButton
                      columns={columnSelectorOptions.filter(
                        c => c.id && c.id !== "checkboxColumn"
                      )}
                      columnVisibility={columnVisibility}
                      onToggle={handleColumnSelectorToggle}
                      excludeColumnIds={["checkboxColumn"]}
                    />
                  ) : null
                }
              />
            }
          />
        </div>
      </div>
      {isAddFieldsModalOpen && (
        <AddPremiumDatasetFieldsModal
          isOpen={isAddFieldsModalOpen}
          onClose={() => setIsAddFieldsModalOpen(false)}
          initialCanvasId={initialCanvasIdForFieldsModal}
          onFieldsCreated={async () => {
            // Refetch so new fields show up in table + Columns dropdown immediately
            await refetchPremiumDatasetFiles();
          }}
        />
      )}
      {isAddDatasetFileModalOpen && (
        <AddPremiumDatasetFile
          datasetId={datasetId}
          isOpen={isAddDatasetFileModalOpen}
          onClose={() => setIsAddDatasetFileModalOpen(false)}
          fileCanvasId={
            data?.data && data.data.length > 0
              ? (data.data[0].canvasId as string)
              : undefined
          }
        />
      )}
      {isAddFolderPathModalOpen && (
        <AddFolderPathModal
          datasetId={datasetId}
          isOpen={isAddFolderPathModalOpen}
          onClose={() => setIsAddFolderPathModalOpen(false)}
          fileCanvasId={
            data?.data && data.data.length > 0
              ? (data.data[0].canvasId as string)
              : undefined
          }
          isGuest={isGuest}
          onCreateSuccess={fileRecord => {
            setDownloadCartFiles([fileRecord]);
            setIsDownloadCartModalOpen(true);
          }}
        />
      )}
      {datasetFilesToBeDeleted?.length > 0 && (
        <DeleteModal
          item={datasetFilesToBeDeleted}
          setItem={setDatasetFilesToBeDeleted}
          deleteFunction={handleConfirmDelete}
          isLoading={isDeleteDatasetFileLoading}
          title="Remove from Dataset"
          confirmText="Remove"
          message={() => (
            <>
              Are you sure you want to remove{" "}
              <strong className="font-semibold">
                {datasetFilesToBeDeleted.map(file => file.name).join(", ")}
              </strong>
              ?
            </>
          )}
        />
      )}
      {datasetFilesToBeEdited && (
        <EditPremiumDatasetFile
          datasetFiles={datasetFilesToBeEdited}
          isOpen={!!datasetFilesToBeEdited}
          isLoading={isFetchDatasetFileLoading}
          onClose={() => {
            setDatasetFilesToBeEdited(null);
            setProjectIds([]);
            setSelectedFiles([]);
            setIsAllFilesSelected(false);
            setDeselectedFileIds(new Set());
          }}
          projectIds={projectIds}
        />
      )}
      {isAddExistingFilesModalOpen && (
        <AddExistingFilesModal
          isOpen={isAddExistingFilesModalOpen}
          datasetId={datasetId}
          onClose={() => setIsAddExistingFilesModalOpen(false)}
          onAddFiles={handleAddExistingFiles}
        />
      )}
      {isDownloadCartModalOpen && (
        <VmetaDownloadCartModal
          isOpen={isDownloadCartModalOpen}
          onClose={() => {
            setIsDownloadCartModalOpen(false);
            setDownloadCartFiles([]);
          }}
          datasetFiles={downloadCartFiles}
          datasetName={datasetData?.data?.name}
          datasetId={datasetId}
        />
      )}
      <Drawer
        id="dataset-settings"
        open={isDatasetSettingsOpen}
        setOpen={setIsDatasetSettingsOpen}
        size={DrawerSize.Big}>
        <EditPremiumDatasetForm
          setOpen={setIsDatasetSettingsOpen}
          dataset={datasetData?.data ?? null}
        />
      </Drawer>
    </main>
  );
}
